import axios from 'axios';
import {API_BASE_URL} from './authApi';

export type LiveSessionStatus = 'starting' | 'live' | 'ended';

export interface LiveSessionBroadcasterSummary {
  id: string;
  name: string;
  profilePicture?: string;
}

export interface LiveSessionSummary {
  id: string;
  broadcaster: LiveSessionBroadcasterSummary;
  previewImageUrl?: string;
  previewCapturedAt?: string;
  roomName: string;
  status: LiveSessionStatus;
  title: string;
  startedAt?: string;
  endedAt?: string;
  viewerCount: number;
  peakViewerCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface StartLiveSessionResponse {
  success: boolean;
  session: LiveSessionSummary;
  publisherToken: string;
  liveKitUrl: string;
}

export interface JoinLiveSessionResponse {
  success: boolean;
  session: LiveSessionSummary;
  viewerToken: string;
  liveKitUrl: string;
}

interface LiveSessionsFeedResponse {
  success: boolean;
  sessions: LiveSessionSummary[];
}

interface EndLiveSessionResponse {
  success: boolean;
  session: LiveSessionSummary;
}

interface UpdateLiveSessionPreviewImageResponse {
  success: boolean;
  session: LiveSessionSummary;
}

type ApiErrorResponse = {
  message?: string;
  error?: string | {message?: string};
};

const liveSessionsClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

const getErrorMessage = (error: unknown, fallbackMessage: string) => {
  if (axios.isAxiosError(error)) {
    const errorData = error.response?.data as ApiErrorResponse | undefined;
    const nestedError =
      typeof errorData?.error === 'string'
        ? errorData.error
        : errorData?.error?.message;

    return errorData?.message || nestedError || error.message || fallbackMessage;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallbackMessage;
};

export const liveSessionsApi = {
  async getFeed(token: string, signal?: AbortSignal): Promise<LiveSessionsFeedResponse> {
    try {
      const response = await liveSessionsClient.get<LiveSessionsFeedResponse>('/live-sessions/feed', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        signal,
      });

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
        throw error;
      }

      throw new Error(getErrorMessage(error, 'Failed to load live sessions'));
    }
  },

  async startSession(token: string, payload: {title?: string}, signal?: AbortSignal): Promise<StartLiveSessionResponse> {
    try {
      const response = await liveSessionsClient.post<StartLiveSessionResponse>('/live-sessions', payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        signal,
      });

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
        throw error;
      }

      throw new Error(getErrorMessage(error, 'Failed to start the live session'));
    }
  },

  async createViewerToken(
    token: string,
    sessionId: string,
    signal?: AbortSignal
  ): Promise<JoinLiveSessionResponse> {
    try {
      const response = await liveSessionsClient.post<JoinLiveSessionResponse>(
        `/live-sessions/${sessionId}/viewer-token`,
        undefined,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          signal,
        }
      );

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
        throw error;
      }

      throw new Error(getErrorMessage(error, 'Failed to join the live session'));
    }
  },

  async updatePreviewImage(
    token: string,
    sessionId: string,
    payload: {uploadSessionId: string},
    signal?: AbortSignal
  ): Promise<UpdateLiveSessionPreviewImageResponse> {
    try {
      const response = await liveSessionsClient.post<UpdateLiveSessionPreviewImageResponse>(
        `/live-sessions/${sessionId}/preview-image`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          signal,
        }
      );

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
        throw error;
      }

      throw new Error(getErrorMessage(error, 'Failed to update the live preview image'));
    }
  },

  async endSession(token: string, sessionId: string, signal?: AbortSignal): Promise<EndLiveSessionResponse> {
    try {
      const response = await liveSessionsClient.post<EndLiveSessionResponse>(
        `/live-sessions/${sessionId}/end`,
        undefined,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          signal,
        }
      );

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
        throw error;
      }

      throw new Error(getErrorMessage(error, 'Failed to end the live session'));
    }
  },
};
